/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { Text, Modal } from '@blend/enterprise-components';

import { BlendBridge } from 'blb-types/blendBridge';
import { ModalContent } from 'blb-components/ModalContent';
import { MeContext } from 'blb-contexts/Me';
import { InstructionBullet } from './InstructionBullet';
import { getPersona } from 'blb-persona/index';
import { getSupportEmailAnchor, getSupportContactPhoneInfo } from 'blb-utils/contact';

export namespace InstructionModal {
  export interface Props {
    closing: BlendBridge.API.Loan['closing'];
    handleClose: () => void;
    isOpen: boolean;
  }
}

export const InstructionModal: React.FC<InstructionModal.Props> = ({ isOpen, handleClose, closing }) => {
  const me = React.useContext(MeContext)!;
  const persona = getPersona(me.principalType);
  const supportInfo = persona.getSupportContactInfo();

  const instructionBullets = (() => {
    const supportBullet = {
      ops_desk_agent: (
        <InstructionBullet>
          <Text>If you need any assistance contact {getSupportEmailAnchor(supportInfo.email)}.</Text>
        </InstructionBullet>
      ),
      settlement_agent: (
        <InstructionBullet>
          <Text>
            For questions about the closing, please contact your lender point of contact. For technical questions about
            this portal, contact {getSupportEmailAnchor(supportInfo.email)}
            {getSupportContactPhoneInfo(supportInfo)}.
          </Text>
        </InstructionBullet>
      ),
    };

    if (me.principalType === 'ops_desk_agent') {
      return (
        <>
          <InstructionBullet>
            Closing documents will be in the Documents section when they are sent by the lender
          </InstructionBullet>
          <InstructionBullet>
            If lender docs are not pre-split into sign ahead vs. day of, download the lender closing docs, ahead vs. day
            based on the lender’s requirements, and then complete the 2 upload tasks (upload sign ahead &amp; upload day
            upload day of).
            <InstructionBullet>
              The day of document upload will be posted to the assigned Settlement Agent.
            </InstructionBullet>
            <InstructionBullet>
              The sign ahead upload will direct you into Docusign to tag. Once you’re done tagging, these documents will
              be posted to the Settlement Agent and the borrower(s).
            </InstructionBullet>
          </InstructionBullet>
          <InstructionBullet>
            If the documents are pre-split into sign ahead vs. day of, complete the “Tag” task for the sign ahead
            documents.
          </InstructionBullet>
          <InstructionBullet>
            All tagged sign ahead documents will be posted to the borrower automatically on the day of closing.
          </InstructionBullet>
          <InstructionBullet>
            If the closing is redrawn, you will likely have to follow the above process again.
          </InstructionBullet>
          {supportBullet.ops_desk_agent}
        </>
      );
    }

    switch (closing?.closingType) {
      case 'HYBRID':
        return (
          <>
            <InstructionBullet>
              Closing documents will be in the Documents section when lender sends them over. You will find closing
              instructions within the documents.
            </InstructionBullet>
            <InstructionBullet>
              You will be notified by email when the borrower(s) completes signing the sign ahead documents, the
              documents will be available in the Documents section. If the borrower(s) do not complete the sign ahead
              documents, you may need to either print the documents or have the borrower bring an electronic device to
              eSign the documents at closing.
            </InstructionBullet>
            <InstructionBullet>
              Upload all the ink-signed documents after closing. These documents will be automatically sent to the
              lender.
            </InstructionBullet>
            {supportBullet.settlement_agent}
          </>
        );
      case 'RON':
        return (
          <>
            <InstructionBullet>
              <Text>
                Closing documents will be in the Documents section when lender sends them over. You will find closing
                instructions within the documents.
              </Text>
            </InstructionBullet>
            <InstructionBullet>
              <Text>
                Upload the title documents for the closing package when prompted in the Overview section. Once you
                complete your upload, the title documents will automatically be sent for closing. No further action is
                required.
              </Text>
            </InstructionBullet>
            <InstructionBullet>
              <Text>
                You will be notified by email when the signed title documents and lender closing documents are available
                in the Documents section.
              </Text>
            </InstructionBullet>
            {supportBullet.settlement_agent}
          </>
        );
      case 'TRADITIONAL':
        return (
          <>
            <InstructionBullet>
              Closing documents will be in the Documents section when lender sends them over. You will find closing
              instructions within the documents.
            </InstructionBullet>
            <InstructionBullet>
              Upload all the ink-signed documents after closing. These documents will be automatically sent to the
              lender.
            </InstructionBullet>
            {supportBullet.settlement_agent}
          </>
        );
      case 'UNDETERMINED':
      default:
        return (
          <>
            <InstructionBullet>
              <Text>
                Closing documents will be in the Documents section when lender sends them over. You will find closing
                instructions within the documents.
              </Text>
            </InstructionBullet>
            <InstructionBullet>
              <Text>You will be notified by email when signed documents are available in the Documents section.</Text>
            </InstructionBullet>
            {supportBullet.settlement_agent}
          </>
        );
    }
  })();
  return (
    <Modal contentLabel="Instructions" handleClose={handleClose} isOpen={isOpen}>
      <ModalContent handleClose={handleClose} headerText="Instructions">
        <ul>{instructionBullets}</ul>
      </ModalContent>
    </Modal>
  );
};
